// CallTonePlayer + CallChimePlayer (ios/Sources/Calls/CallTones.swift) on WebAudio: the same
// synthesized sounds (core/callTones), rendered once into a buffer and looped or played once.
// The apps ship no audio assets; neither does the web.
import { CallTones, type TonePattern } from '../../../core/callTones';

let context: AudioContext | null = null;

/** The one AudioContext for call sounds. Browsers start it suspended until the page was used. */
export function audioContext(): AudioContext | null {
  if (typeof AudioContext === 'undefined') return null;
  context ??= new AudioContext();
  if (context.state === 'suspended') void context.resume().catch(() => undefined);
  return context;
}

const buffers = new Map<string, AudioBuffer>();

function toneBuffer(ctx: AudioContext, key: string, pattern: TonePattern): AudioBuffer {
  const cached = buffers.get(key);
  if (cached && cached.sampleRate === ctx.sampleRate) return cached;
  const frames = CallTones.cycleFrames(pattern, ctx.sampleRate);
  const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let f = 0; f < frames; f++) data[f] = CallTones.toneSample(pattern, f, ctx.sampleRate);
  buffers.set(key, buffer);
  return buffer;
}

/** A looping tone (the ring, the ringback). `start` replaces whatever was playing. */
export class TonePlayer {
  private source: AudioBufferSourceNode | null = null;

  start(key: 'ring' | 'ringback'): void {
    this.stop();
    const ctx = audioContext();
    if (!ctx) return;
    const source = ctx.createBufferSource();
    source.buffer = toneBuffer(ctx, key, key === 'ring' ? CallTones.incomingRing : CallTones.ringback);
    source.loop = true;
    source.connect(ctx.destination);
    source.start();
    this.source = source;
  }

  stop(): void {
    try {
      this.source?.stop();
    } catch {
      /* never started */
    }
    this.source?.disconnect();
    this.source = null;
  }

  get playing(): boolean {
    return this.source !== null;
  }
}

/** The two-note join/leave chime, played once. */
export function playChime(kind: 'join' | 'leave'): void {
  const ctx = audioContext();
  if (!ctx) return;
  const samples = CallTones.chimeSamples(kind, ctx.sampleRate);
  const buffer = ctx.createBuffer(1, samples.length, ctx.sampleRate);
  buffer.getChannelData(0).set(samples);
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.connect(ctx.destination);
  source.start();
}
